import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/shared/lib/supabase';
import type { SupportTicketSummary, SupportTicketMessage } from '@/shared/types';

export interface SupportTicketFilters {
  status?: string[];
  priority?: string[];
  category?: string[];
  clientId?: string | null;
}

const mapTicket = (raw: any): SupportTicketSummary => ({
  id: raw.id,
  title: raw.title,
  description: raw.description,
  category: raw.category,
  priority: raw.priority,
  status: raw.status,
  created_at: raw.created_at,
  updated_at: raw.updated_at,
  user_id: raw.user_id,
  client_product_id: raw.client_product_id,
  assigned_admin_id: raw.assigned_admin_id ?? null,
  notion_page_url: raw.notion_page_url ?? null,
  user: raw.user ?? null,
});

export const useAdminSupportTickets = (filters: SupportTicketFilters) => {
  return useQuery<SupportTicketSummary[]>({
    queryKey: ['admin-support-tickets', filters],
    queryFn: async () => {
      let query = supabase
        .from('support_tickets')
        // Avoid embedding auth.users directly (PostgREST may restrict this), select user_id and enrich with user_profiles
        .select('*, client_product:client_products(id, product:products(name))')
        .order('created_at', { ascending: false });

      if (filters.status && filters.status.length) {
        query = query.in('status', filters.status);
      }

      if (filters.priority && filters.priority.length) {
        query = query.in('priority', filters.priority);
      }

      if (filters.category && filters.category.length) {
        query = query.in('category', filters.category);
      }

      if (filters.clientId) {
        query = query.eq('user_id', filters.clientId);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Attach user_profiles to the user objects
      const userIds = Array.from(new Set((data || []).map((d: any) => d.user_id).filter(Boolean)));
      const profilesMap: Record<string, any> = {};
      if (userIds.length > 0) {
        const { data: profilesData, error: profilesErr } = await supabase
          .from('user_profiles')
          .select('id, full_name, company_name')
          .in('id', userIds);
        if (!profilesErr && profilesData) {
          profilesData.forEach((p: any) => { profilesMap[String(p.id)] = p; });
        }
      }

      const enriched = (data || []).map((d: any) => {
        const uid = d.user_id;
        if (uid) {
          d.user = d.user || {};
          d.user.profile = profilesMap[String(uid)] || null;
        }
        return d;
      });

      return enriched.map(mapTicket);
    },
  });
};

export const useTicketMessages = (ticketId: string | null) => {
  return useQuery<SupportTicketMessage[]>({
    queryKey: ['admin-ticket-messages', ticketId],
    enabled: !!ticketId,
    queryFn: async () => {
      if (!ticketId) return [];

      const { data, error } = await supabase
        .from('ticket_messages')
        // Fetch base message rows and enrich authors below
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Enrich messages with user_profiles
      const userIds = Array.from(new Set((data || []).map((d: any) => d.user_id).filter(Boolean)));
      const profilesMap: Record<string, any> = {};
      if (userIds.length > 0) {
        const { data: profilesData, error: profilesErr } = await supabase
          .from('user_profiles')
          .select('id, full_name, role')
          .in('id', userIds);
        if (!profilesErr && profilesData) {
          profilesData.forEach((p: any) => { profilesMap[String(p.id)] = p; });
        }
      }

      const enrichedMessages = (data || []).map((item) => {
        if (item.user_id) {
          item.author = item.author || {};
          item.author.profile = profilesMap[String(item.user_id)] || null;
        }
        return item;
      });

      return (enrichedMessages ?? []).map((item) => ({
        id: item.id,
        ticket_id: item.ticket_id,
        user_id: item.user_id,
        message: item.message,
        created_at: item.created_at,
        is_internal: item.is_internal ?? false,
        created_by_role: item.created_by_role,
        author: item.author
          ? {
              full_name: item.author.profile?.full_name,
              email: undefined,
              role: item.author.profile?.role ?? item.author.role,
            }
          : undefined,
      }));
    },
  });
};

export const useUpdateSupportTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<SupportTicketSummary> & { id: string }) => {
      const { data, error } = await supabase
        .from('support_tickets')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return mapTicket(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-support-tickets'] });
    },
  });
};

export const useCreateTicketMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      ticket_id: string;
      message: string;
      user_id?: string | null;
      is_internal?: boolean;
      created_by_role?: string;
    }) => {
      const { data, error } = await supabase
        .from('ticket_messages')
        .insert({
          ticket_id: payload.ticket_id,
          message: payload.message,
          user_id: payload.user_id ?? null,
          is_internal: payload.is_internal ?? false,
          created_by_role: payload.created_by_role ?? 'admin',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-ticket-messages', variables.ticket_id] });
      queryClient.invalidateQueries({ queryKey: ['admin-support-tickets'] });
    },
  });
};
